import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FiMic, FiPaperclip, FiPhone, FiPhoneOff, FiSend, FiX, FiMinus, FiMessageCircle } from "react-icons/fi";
import csrOutreachService from "../../api/services/csrOutreachService";
import { getSupportSocket } from "../../api/services/supportSocket";
import { useVoiceRecorder } from "../../hooks/useVoiceRecorder";
import VoiceNote from "../../components/VoiceNote";
import {
  joinSupportAgoraCall,
  leaveSupportAgoraCall,
  parseSupportAgoraSession,
  ensureMicrophonePermission,
  formatSupportCallError,
} from "../../utils/supportAgoraCall";
import {
  startSupportCallRingback,
  stopSupportCallRingback,
  disposeSupportCallRingback,
  DEFAULT_MAX_RING_BELLS,
} from "../../utils/supportCallRingback";
import { setCsrActiveCallSession } from "../../utils/csrActiveCallSession";
import { notifyDailyTargetsChanged } from "../../utils/dailyTargetsEvents";
import { ChatSkeleton } from "../../components/common/skeletons";

const MAX_RING_BELLS = DEFAULT_MAX_RING_BELLS;

const THEME = { primary: "#FB565F" };

function getAdminId() {
  try {
    const raw = localStorage.getItem("adminUserDetails");
    if (!raw) return null;
    const id = JSON.parse(raw)?.id;
    return id != null ? Number(id) : null;
  } catch {
    return null;
  }
}

function ButtonSpinner({ light = false }) {
  return (
    <span
      className={`inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 ${
        light ? "border-white/40 border-t-white" : "border-current border-t-transparent"
      }`}
      aria-hidden
    />
  );
}

function formatRecordingTime(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function formatCallElapsed(seconds) {
  const sec = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function messageCreatedAt(m) {
  const raw = m?.created_date || m?.created_at || m?.createdAt || m?.updated_at;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatBubbleTime(date) {
  if (!date) return "";
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatDayLabel(date) {
  if (!date) return "";
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(date, today)) return "Today";
  if (sameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function dayKey(date) {
  if (!date) return "";
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/** Gifted Chat-style: own messages on the right (agent), customer on the left. */
function isMineMessage(m, adminId) {
  if (m?.isAgent === true || m?.user_type === "AGENT" || m?.user_type === "CSR") return true;
  if (m?.user_type === "USER" || m?.user_type === "CUSTOMER") return false;
  if (adminId != null && m?.user != null && Number(m.user) === Number(adminId)) return true;
  return false;
}

function normalizeChatMessage(m, adminId) {
  if (!m) return m;
  const isMine = isMineMessage(m, adminId);
  const type =
    m.call_id != null || m.type === "CALL"
      ? "CALL"
      : m.type || (m.image ? "IMAGE" : m.voice ? "VOICE" : "TEXT");
  return {
    ...m,
    type,
    isAgent: isMine,
    isMine,
    createdAt: messageCreatedAt(m),
  };
}

/** API returns newest-first (DESC); Gifted Chat expects oldest → newest. */
function toChronological(list, adminId) {
  const rows = Array.isArray(list) ? list : [];
  const chronological = [...rows].reverse();
  return chronological.map((m) => normalizeChatMessage(m, adminId));
}

function formatCallSubtitle(m) {
  const status = String(m.call_status || "").toLowerCase();
  const duration = Number(m.call_duration_seconds ?? m.callDurationSeconds);
  if (Number.isFinite(duration) && duration > 0) {
    const mins = Math.floor(duration / 60);
    const secs = duration % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
  }
  if (status === "rejected" || status === "declined") return "Call declined";
  if (status === "no_answer" || status === "missed" || status === "cancelled" || status === "canceled") {
    return "No answer";
  }
  return "Voice call";
}

export default function CsrCustomerChat({ customer, onClose, onCallEnded }) {
  const adminId = getAdminId();
  const [messages, setMessages] = useState([]);
  const [chatId, setChatId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [isOnline, setIsOnline] = useState(!!customer?.isOnline);
  const [canCall, setCanCall] = useState(!!customer?.isOnline);
  const [startingCall, setStartingCall] = useState(false);
  const [endingCall, setEndingCall] = useState(false);
  const [sendingVoice, setSendingVoice] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [callPhase, setCallPhase] = useState("idle");
  const [callElapsed, setCallElapsed] = useState(0);
  const [ringBellCount, setRingBellCount] = useState(0);
  const [minimized, setMinimized] = useState(false);
  const callClientRef = useRef(null);
  const callStartRef = useRef(0);
  const customerJoinedRef = useRef(false);
  const activeCallRef = useRef(null);
  const endingCallRef = useRef(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);
  const userId = customer?.userId;

  const appendMessage = useCallback(
    (msg) => {
      const normalized = normalizeChatMessage(msg, adminId);
      setMessages((prev) => {
        const id = normalized.id || normalized.messageId;
        if (id && prev.some((m) => m.id === id)) {
          return prev.map((m) => (m.id === id ? { ...m, ...normalized } : m));
        }
        return [...prev, normalized];
      });
      shouldStickToBottomRef.current = true;
    },
    [adminId]
  );

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  const loadChat = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const contact = await csrOutreachService.contact(userId);
      setChatId(contact.chatId);
      setIsOnline(!!contact.isOnline);
      setCanCall(!!contact.canCall);
      const data = await csrOutreachService.getChat(userId);
      shouldStickToBottomRef.current = true;
      setMessages(toChronological(data.messages || [], adminId));
      setIsOnline(!!data.isOnline);
      setCanCall(!!data.canCall);
      return data.activeCall || contact.activeCall || null;
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to open chat");
      onClose?.();
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, onClose, adminId]);

  const cleanupCallLocal = useCallback(async () => {
    const call = activeCallRef.current;
    if (!call || endingCallRef.current) return;
    endingCallRef.current = true;
    setEndingCall(true);
    stopSupportCallRingback();
    try {
      await leaveSupportAgoraCall(callClientRef.current, callClientRef.current?.localAudioTrack);
    } finally {
      callClientRef.current = null;
      activeCallRef.current = null;
      setActiveCall(null);
      setCallPhase("idle");
      callStartRef.current = 0;
      customerJoinedRef.current = false;
      setRingBellCount(0);
      endingCallRef.current = false;
      setEndingCall(false);
      setMinimized(false);
      loadChat();
      onCallEnded?.();
    }
  }, [loadChat, onCallEnded]);

  const handleCustomerJoinedCall = useCallback(() => {
    if (customerJoinedRef.current) return;
    customerJoinedRef.current = true;
    stopSupportCallRingback();
    setCallPhase("active");
    callStartRef.current = Date.now();
    setRingBellCount(0);
    toast.success("Customer answered");
  }, []);

  const restoreActiveCallIfNeeded = useCallback(
    async (activeCallData) => {
      if (!activeCallData?.channelName || activeCallRef.current || endingCallRef.current) {
        return;
      }
      const status = String(activeCallData.status || "").toLowerCase();
      if (!["ringing", "accepted", "initiated"].includes(status)) return;

      setActiveCall(activeCallData);
      activeCallRef.current = activeCallData;

      if (status === "accepted") {
        customerJoinedRef.current = true;
        setCallPhase("active");
        const respondedAt = activeCallData.respondedAt
          ? new Date(activeCallData.respondedAt).getTime()
          : Date.now();
        callStartRef.current = Number.isFinite(respondedAt) ? respondedAt : Date.now();
      } else {
        setCallPhase("ringing");
      }

      if (callClientRef.current) return;

      try {
        const session = parseSupportAgoraSession(activeCallData);
        const { client, localAudioTrack } = await joinSupportAgoraCall(session, {
          onRemoteUserJoined: handleCustomerJoinedCall,
          onRemoteUserLeft: () => {
            cleanupCallLocal();
          },
        });
        callClientRef.current = client;
        callClientRef.current.localAudioTrack = localAudioTrack;
      } catch (e) {
        console.warn("Could not rejoin active CSR call", e);
      }
    },
    [cleanupCallLocal, handleCustomerJoinedCall]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const activeCallData = await loadChat();
      if (!cancelled && activeCallData) {
        await restoreActiveCallIfNeeded(activeCallData);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadChat, restoreActiveCallIfNeeded]);

  useEffect(() => {
    if (!shouldStickToBottomRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!chatId) return undefined;
    const socket = getSupportSocket();
    const onMessage = (msg) => {
      if (Number(msg.chat_id || msg.chatId) !== Number(chatId)) return;
      appendMessage(msg);
    };
    socket.on("message", onMessage);
    return () => socket.off("message", onMessage);
  }, [chatId, appendMessage]);

  useEffect(() => {
    const socket = getSupportSocket();
    const onCallEnded = (payload) => {
      const channelName = String(payload?.channelName || "").trim();
      if (!channelName || channelName !== activeCallRef.current?.channelName) return;
      cleanupCallLocal();
    };
    const onCallAccepted = (payload) => {
      const channelName = String(payload?.channelName || "").trim();
      if (!channelName || channelName !== activeCallRef.current?.channelName) return;
      handleCustomerJoinedCall();
    };
    socket.on("chatCallEnded", onCallEnded);
    socket.on("chatCallAccepted", onCallAccepted);
    return () => {
      socket.off("chatCallEnded", onCallEnded);
      socket.off("chatCallAccepted", onCallAccepted);
    };
  }, [cleanupCallLocal, handleCustomerJoinedCall]);

  useEffect(
    () => () => {
      disposeSupportCallRingback();
      leaveSupportAgoraCall(callClientRef.current, callClientRef.current?.localAudioTrack);
    },
    []
  );

  useEffect(() => {
    if (callPhase !== "active" || !callStartRef.current) {
      setCallElapsed(0);
      return undefined;
    }
    const tick = () => setCallElapsed(Math.floor((Date.now() - callStartRef.current) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [callPhase]);

  const finalizeEndCall = useCallback(
    async ({ duration, noAnswer = false }) => {
      if (!activeCallRef.current || endingCallRef.current) return;
      if (noAnswer && customerJoinedRef.current) return;
      const endedCall = activeCallRef.current;
      endingCallRef.current = true;
      setEndingCall(true);
      stopSupportCallRingback();
      let keepCallActive = false;
      try {
        await csrOutreachService.endCall(userId, {
          channelName: endedCall.channelName,
          durationSeconds: duration,
          callId: endedCall.callId,
        });
        await leaveSupportAgoraCall(callClientRef.current, callClientRef.current?.localAudioTrack);
        if (noAnswer) {
          toast.error("No answer — call ended");
        } else if (duration > 0) {
          toast.success(`Call logged (${Math.floor(duration / 60)}m ${duration % 60}s)`);
          notifyDailyTargetsChanged({ team: "csr", action: "call_logged" });
        }
        onCallEnded?.();
      } catch (e) {
        if (noAnswer && e?.response?.status === 409 && e?.response?.data?.answered) {
          keepCallActive = true;
          endingCallRef.current = false;
          setEndingCall(false);
          handleCustomerJoinedCall();
          return;
        }
        await leaveSupportAgoraCall(callClientRef.current, callClientRef.current?.localAudioTrack);
      } finally {
        if (!keepCallActive) {
          callClientRef.current = null;
          activeCallRef.current = null;
          setActiveCall(null);
          setCallPhase("idle");
          callStartRef.current = 0;
          customerJoinedRef.current = false;
          setRingBellCount(0);
          endingCallRef.current = false;
          setEndingCall(false);
          setMinimized(false);
          loadChat();
        }
      }
    },
    [userId, loadChat, onCallEnded, handleCustomerJoinedCall]
  );

  const endCallNoAnswer = useCallback(() => {
    if (customerJoinedRef.current) return;
    finalizeEndCall({ duration: 0, noAnswer: true });
  }, [finalizeEndCall]);

  const sendMessage = async () => {
    if (!text.trim() || sending) return;
    const payload = text.trim();
    setSending(true);
    setText("");
    try {
      const res = await csrOutreachService.sendMessage(userId, payload);
      if (res.message) appendMessage(res.message);
    } catch (e) {
      setText(payload);
      toast.error(e?.response?.data?.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const sendVoiceMessage = useCallback(
    async (blob) => {
      setSendingVoice(true);
      try {
        const res = await csrOutreachService.sendVoice(userId, blob);
        if (res.message) appendMessage(res.message);
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed to send voice");
      } finally {
        setSendingVoice(false);
      }
    },
    [userId, appendMessage]
  );

  const { recording, recordingTime, startRecording, stopRecording, cancelRecording } =
    useVoiceRecorder(sendVoiceMessage, {
      onRecordingTooShort: () => toast.error("Recording too short"),
    });

  const sendImage = async (file) => {
    if (!file?.type?.startsWith("image/")) {
      toast.error("Please choose an image");
      return;
    }
    setSending(true);
    try {
      const res = await csrOutreachService.sendImage(userId, file);
      if (res.message) appendMessage(res.message);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to send image");
    } finally {
      setSending(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const startCall = async () => {
    if (!canCall || !isOnline) {
      toast.error("User is offline — messages only");
      return;
    }
    if (startingCall || activeCall) return;

    const micPermission = await ensureMicrophonePermission();
    if (!micPermission.granted) {
      toast.error("Please allow microphone access to start a call.");
      return;
    }

    setStartingCall(true);
    let initiatedCall = null;
    try {
      await leaveSupportAgoraCall(callClientRef.current, callClientRef.current?.localAudioTrack);
      callClientRef.current = null;

      const res = await csrOutreachService.initiateCall(userId);
      initiatedCall = res;
      const session = parseSupportAgoraSession(res);
      const { client, localAudioTrack } = await joinSupportAgoraCall(session, {
        onRemoteUserJoined: handleCustomerJoinedCall,
        onRemoteUserLeft: () => {
          cleanupCallLocal();
        },
      });
      callClientRef.current = client;
      callClientRef.current.localAudioTrack = localAudioTrack;
      setActiveCall(res);
      customerJoinedRef.current = false;
      callStartRef.current = 0;
      setRingBellCount(0);
      setCallPhase("ringing");
      await startSupportCallRingback({
        maxRings: MAX_RING_BELLS,
        onRing: (count) => setRingBellCount(count),
        onComplete: () => endCallNoAnswer(),
      });
    } catch (e) {
      await leaveSupportAgoraCall(callClientRef.current, callClientRef.current?.localAudioTrack);
      callClientRef.current = null;
      setActiveCall(null);
      setCallPhase("idle");
      if (initiatedCall?.channelName) {
        try {
          await csrOutreachService.endCall(userId, {
            channelName: initiatedCall.channelName,
            durationSeconds: 0,
            callId: initiatedCall.callId,
          });
        } catch {
          // ignore cleanup errors
        }
      }
      toast.error(formatSupportCallError(e));
    } finally {
      setStartingCall(false);
    }
  };

  const endCall = async () => {
    if (!activeCall || endingCallRef.current) return;
    const duration = callStartRef.current
      ? Math.floor((Date.now() - callStartRef.current) / 1000)
      : 0;
    await finalizeEndCall({ duration, noAnswer: false });
  };

  const renderMessage = (m, index) => {
    const isMine = m.isMine === true || isMineMessage(m, adminId);
    const createdAt = m.createdAt || messageCreatedAt(m);
    const prev = messages[index - 1];
    const prevDay = dayKey(prev?.createdAt || messageCreatedAt(prev));
    const thisDay = dayKey(createdAt);
    const showDay = Boolean(thisDay && thisDay !== prevDay);
    const timeLabel = formatBubbleTime(createdAt);
    const callMedia = String(m.call_type || m.call_category || "voice").toLowerCase().includes("video")
      ? "Video call"
      : "Voice call";

    return (
      <div key={m.id || `${index}-${m.message || ""}`}>
        {showDay && (
          <div className="flex justify-center my-3">
            <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {formatDayLabel(createdAt)}
            </span>
          </div>
        )}
        <div className={`flex mb-2 ${isMine ? "justify-end" : "justify-start"}`}>
          <div className={`max-w-[78%] flex flex-col ${isMine ? "items-end" : "items-start"}`}>
            <div
              className={`rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                isMine
                  ? "text-white rounded-br-sm"
                  : "bg-gray-100 text-gray-800 rounded-bl-sm border border-gray-200"
              }`}
              style={isMine ? { backgroundColor: THEME.primary } : {}}
            >
              {(m.type === "TEXT" || (!m.type && m.message)) && m.type !== "IMAGE" && m.type !== "VOICE" && m.type !== "CALL" && (
                <p className="whitespace-pre-wrap break-words leading-snug">{m.message}</p>
              )}
              {m.type === "IMAGE" && m.image && (
                <a href={m.image} target="_blank" rel="noopener noreferrer" className="block">
                  <img src={m.image} alt="" className="max-h-52 rounded-lg object-cover" />
                </a>
              )}
              {m.type === "VOICE" && m.voice && (
                <div className={isMine ? "voice-note-on-primary" : ""}>
                  <VoiceNote audioUrl={m.voice} />
                </div>
              )}
              {m.type === "CALL" && (
                <div className="flex items-center gap-2 min-w-[140px]">
                  <FiPhone className="shrink-0 opacity-90" />
                  <div>
                    <p className="font-semibold text-[13px] leading-tight">{callMedia}</p>
                    <p className={`text-[11px] mt-0.5 ${isMine ? "text-white/85" : "text-gray-500"}`}>
                      {formatCallSubtitle(m)}
                    </p>
                  </div>
                </div>
              )}
            </div>
            {timeLabel && (
              <span className={`text-[10px] mt-1 px-1 ${isMine ? "text-gray-400" : "text-gray-400"}`}>
                {timeLabel}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const name = `${customer?.firstName || ""} ${customer?.lastName || ""}`.trim();
  const inCallUi = Boolean(activeCall || endingCall || callPhase !== "idle");
  const callStatusLabel = endingCall
    ? "Ending…"
    : callPhase === "active"
      ? formatCallElapsed(callElapsed)
      : callPhase === "ringing"
        ? `Ring ${ringBellCount}/${MAX_RING_BELLS}`
        : "";

  const handleClose = () => {
    if (inCallUi) {
      setMinimized(true);
      return;
    }
    onClose?.();
  };

  useEffect(() => {
    if (inCallUi && activeCall) {
      setCsrActiveCallSession({
        userId,
        customer,
        callPhase,
        callStatusLabel,
        channelName: activeCall.channelName,
      });
      return;
    }
    setCsrActiveCallSession(null);
  }, [inCallUi, activeCall, callPhase, callStatusLabel, userId, customer]);

  if (minimized && inCallUi) {
    return (
      <div className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 rounded-2xl bg-white shadow-2xl border border-gray-200 px-3 py-2.5 max-w-[min(100vw-2rem,360px)]">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100">
          <FiPhone className="text-emerald-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-gray-900 truncate">{name || "Customer"}</p>
          <p className="text-sm font-mono font-bold text-emerald-600 tabular-nums">{callStatusLabel}</p>
        </div>
        <button
          type="button"
          onClick={() => setMinimized(false)}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          title="Expand chat"
          aria-label="Expand chat"
        >
          <FiMessageCircle className="text-gray-600" />
        </button>
        <button
          type="button"
          onClick={endCall}
          disabled={endingCall}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white disabled:opacity-70"
        >
          {endingCall ? <ButtonSpinner light /> : <FiPhoneOff />}
        </button>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 p-4 ${inCallUi ? "pointer-events-none" : "flex items-center justify-center bg-black/50"}`}>
      <div className={`bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh] pointer-events-auto ${inCallUi ? "fixed bottom-4 right-4 max-h-[min(90vh,520px)]" : ""}`}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              {name || "Customer"}
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                  isOnline ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-500"
                }`}
              >
                {isOnline ? "Online" : "Offline"}
              </span>
            </h3>
            <p className="text-xs text-gray-400">{customer?.email}</p>
            {!canCall && (
              <p className="text-[11px] text-amber-700 mt-0.5">Offline — messages only (no calls)</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {(activeCall || endingCall || callPhase !== "idle") ? (
              <>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-800 text-xs font-mono font-bold tabular-nums">
                  <span
                    className={`h-2 w-2 rounded-full ${
                      endingCall
                        ? "bg-gray-400"
                        : callPhase === "active"
                          ? "bg-emerald-500 animate-pulse"
                          : "bg-amber-500 animate-pulse"
                    }`}
                  />
                  {endingCall
                    ? "Ending…"
                    : callPhase === "active"
                      ? formatCallElapsed(callElapsed)
                      : `Ring ${ringBellCount}/${MAX_RING_BELLS}`}
                </span>
                <button
                  type="button"
                  onClick={endCall}
                  disabled={endingCall}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white disabled:opacity-70 min-w-[72px] justify-center"
                >
                  {endingCall ? (
                    <>
                      <ButtonSpinner light />
                      Ending…
                    </>
                  ) : (
                    <>
                      <FiPhoneOff /> End
                    </>
                  )}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={startCall}
                disabled={startingCall || !canCall}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-600 text-white disabled:opacity-70 min-w-[72px] justify-center"
                title={canCall ? "Call user" : "User offline — cannot call"}
              >
                {startingCall ? (
                  <>
                    <ButtonSpinner light />
                    Calling…
                  </>
                ) : (
                  <>
                    <FiPhone /> Call
                  </>
                )}
              </button>
            )}
            {inCallUi && (
              <button
                type="button"
                onClick={() => setMinimized(true)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                title="Minimize — keep browsing"
                aria-label="Minimize call"
              >
                <FiMinus />
              </button>
            )}
            <button type="button" onClick={handleClose} className="p-2 rounded-lg hover:bg-gray-100" title={inCallUi ? "Minimize" : "Close"}>
              <FiX />
            </button>
          </div>
        </div>

        {callPhase === "ringing" && (
          <p className="text-center text-xs text-amber-700 bg-amber-50 py-2 flex items-center justify-center gap-2">
            <span>Ringing…</span>
            <span className="font-mono font-bold tabular-nums">
              {ringBellCount}/{MAX_RING_BELLS}
            </span>
          </p>
        )}
        {callPhase === "active" && (
          <p className="text-center text-xs text-emerald-700 bg-emerald-50 py-2 flex items-center justify-center gap-2">
            <span>Call in progress</span>
            <span className="font-mono font-bold tabular-nums">{formatCallElapsed(callElapsed)}</span>
          </p>
        )}

        {inCallUi && (
          <p className="text-center text-[11px] text-gray-400 py-1">
            Minimize to keep using CSR Outreach while on a call
          </p>
        )}

        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto p-4 min-h-[280px] bg-[#f7f7f8]"
          onScroll={(e) => {
            const el = e.currentTarget;
            const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            shouldStickToBottomRef.current = distFromBottom < 80;
          }}
        >
          {loading ? (
            <ChatSkeleton rows={8} />
          ) : messages.length === 0 ? (
            <div className="h-full min-h-[220px] flex items-center justify-center">
              <p className="text-sm text-gray-400 text-center px-6">
                No messages yet. Say hello to start the conversation.
              </p>
            </div>
          ) : (
            <>
              {messages.map((m, index) => renderMessage(m, index))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <div className="border-t p-3">
          {recording ? (
            <div className="flex items-center gap-2">
              <span className="text-red-600 text-sm font-semibold animate-pulse">
                {formatRecordingTime(recordingTime)}
              </span>
              <button
                type="button"
                onClick={stopRecording}
                disabled={sendingVoice}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-70 min-w-[108px] justify-center"
                style={{ backgroundColor: THEME.primary }}
              >
                {sendingVoice ? (
                  <>
                    <ButtonSpinner light />
                    Sending…
                  </>
                ) : (
                  "Send voice"
                )}
              </button>
              <button
                type="button"
                onClick={cancelRecording}
                disabled={sendingVoice}
                className="px-3 py-2 text-sm text-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && sendImage(e.target.files[0])}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending}
                className="p-2 rounded-lg bg-gray-100 text-gray-600 disabled:opacity-50"
                title="Attach image"
              >
                <FiPaperclip />
              </button>
              <button
                type="button"
                onClick={startRecording}
                disabled={sending || sendingVoice || endingCall}
                className="p-2 rounded-lg bg-gray-100 text-gray-600 disabled:opacity-50"
              >
                <FiMic />
              </button>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Type a message…"
                rows={1}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none resize-none max-h-28 min-h-[40px]"
                disabled={sending}
              />
              <button
                type="button"
                onClick={sendMessage}
                disabled={!text.trim() || sending}
                className="inline-flex items-center justify-center p-2 rounded-lg text-white disabled:opacity-50 min-w-[36px]"
                style={{ backgroundColor: THEME.primary }}
              >
                {sending ? <ButtonSpinner light /> : <FiSend />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
